// ─── Champs « à la carte » d'un contact ───────────────────────────
// Propriétés optionnelles ajoutées d'un clic depuis un catalogue (organisation,
// fonction, site web, anniversaire…) + un champ 100 % PERSONNALISÉ (nom + emoji +
// TYPE de saisie libres). Chaque entrée : { cle, nom, emoji, saisie, valeur }.
// Les choix (date, heure, jour, catalogue de champs) passent tous par des
// modales SŒURS remontées à l'écran — jamais imbriquées dans la feuille.
import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { LigneSwipe, confirmerSuppression, ChoixPuces, stylesForm, frJMA, useUI, useT } from '@mop/core';
import { CHAMPS_CONTACT, SAISIES_CHAMP, saisieDef, estChampPerso, nouvelIdEntree,
  JOURS_HORAIRE, jourHoraire, parseHoraires, canonHoraires } from './services/contacts';

// `catalogue` : la liste de champs proposes. Par defaut celui des CONTACTS —
// le composant est generique, seul son catalogue change d'un objet a l'autre
// (un evenement propose « Lien visio » ou « Reservation », pas « Organisation »).
export default function ChampsPerso({ entries = [], onChange, onDemanderDate, onDemanderHeure, onDemanderChoix, catalogue = CHAMPS_CONTACT }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const maj = (arr) => onChange?.(arr);
  const setEntry = (i, patch) => maj(entries.map((e, k) => (k === i ? { ...e, ...patch } : e)));
  const retirer = (i) => maj(entries.filter((_, k) => k !== i));
  const dispo = (catalogue || []).filter(c => !entries.some(e => e.cle === c.cle));

  const ajouterCatalogue = (c) =>
    maj([...entries, { cle: c.cle, nom: c.nom, emoji: c.emoji, saisie: c.saisie || 'texte', valeur: '' }]);
  // L'icone suit le TYPE choisi (email → ✉️, lien → 🌐…) tant que l'utilisateur
  // ne l'a pas personnalisee : on ne remplace que l'icone par defaut (🏷️) ou
  // celle du type precedent — une icone choisie a la main est respectee.
  const emojiSuivantType = (e, nouveauType) => {
    const auto = e.emoji === '🏷️' || e.emoji === saisieDef(e.saisie).emoji;
    return auto ? saisieDef(nouveauType).emoji : e.emoji;
  };

  // Ligne dont on modifie le NOM et le TYPE (null = aucune). Le swipe → l'ouvre.
  const [edite, setEdite] = useState(null);

  // Ouvrir l'edition d'un champ du CATALOGUE le CONVERTIT en champ perso
  // (arbitrage de Cedric, 09/08). Sa cle change donc pour `perso_…` : c'est
  // elle qui porte l'appartenance, et un champ renomme n'a plus de raison de
  // suivre les evolutions d'un catalogue auquel il ne ressemble plus.
  // La VALEUR saisie est conservee — on ne perd jamais ce que l'utilisateur a
  // tape en changeant l'etiquette.
  const ouvrirEdition = (i) => {
    const e = entries[i];
    if (!estChampPerso(e.cle)) setEntry(i, { cle: `perso_${nouvelIdEntree()}` });
    setEdite(i);
  };

  const ajouterPerso = () =>
    maj([...entries, { cle: `perso_${nouvelIdEntree()}`, nom: '', emoji: '🏷️', saisie: 'texte', valeur: '' }]);

  // Catalogue en fenetre : « Personnalise » ferme la liste des propositions.
  const demanderChamp = () => onDemanderChoix?.({
    titre: t('contacts.ajouterChamp'), emoji: '🏷️',
    options: [
      ...dispo.map(c => ({ id: c.cle, libelle: `${c.emoji}  ${c.nom}` })),
      { id: '__perso', libelle: `✳️  ${t('contacts.champPerso')}` },
    ],
    onChoisir: (id) => (id === '__perso' ? ajouterPerso() : ajouterCatalogue(dispo.find(c => c.cle === id))),
  });
  const choisirDate = (i, valeur) => onDemanderDate?.({ valeur, onChoisir: (dk) => setEntry(i, { valeur: dk }) });

  // Horaire : jour (ou « tous ») + debut + fin sur UNE ligne, et AUTANT DE
  // LIGNES qu'il y a de plages (matin/apres-midi, jours differents…).
  // Retrait d'une plage par une CROIX : derogation assumee a la regle app-wide
  // (on supprime au swipe), car le swipe du bloc sert deja a retirer le champ
  // entier et l'appui long — seule alternative — ne se devine pas.
  const ligneHoraire = (e, i) => {
    const plages = parseHoraires(e.valeur);
    const poser = (k, patch) => setEntry(i, {
      valeur: canonHoraires(plages.map((p, n) => (n === k ? { ...p, ...patch } : p))),
    });
    const ajouterPlage = () => setEntry(i, {
      // La nouvelle plage reprend le jour de la precedente : on ajoute le plus
      // souvent un second creneau du MEME jour.
      valeur: canonHoraires([...plages, { jour: plages[plages.length - 1]?.jour || 'tous', debut: '', fin: '' }]),
    });
    const retirerPlage = (k) => confirmerSuppression(t('commun.supprimer'), t('contacts.horaire'),
      () => setEntry(i, { valeur: canonHoraires(plages.filter((_, n) => n !== k)) }));

    return (
      <View>
        {plages.map((h, k) => (
          <View key={k} style={s.horLigne}>
            <TouchableOpacity style={[s.horBtn, { flex: 1.1 }]} activeOpacity={0.7}
              onPress={() => onDemanderChoix?.({
                titre: t('contacts.jour'), emoji: '📅',
                options: JOURS_HORAIRE.map(j => ({ id: j.id, libelle: j.label })),
                onChoisir: (id) => poser(k, { jour: id }),
              })}>
              <Text style={s.horTxt} numberOfLines={1}>{jourHoraire(h.jour).court}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[s.horBtn, { flex: 1 }]} activeOpacity={0.7}
              onPress={() => onDemanderHeure?.({ valeur: h.debut, onChoisir: (v) => poser(k, { debut: v || '' }) })}>
              <Text style={[s.horTxt, !h.debut && { color: UI.faint }]}>{h.debut || '--:--'}</Text>
            </TouchableOpacity>
            <Text style={s.horFleche}>→</Text>
            <TouchableOpacity style={[s.horBtn, { flex: 1 }]} activeOpacity={0.7}
              onPress={() => onDemanderHeure?.({ valeur: h.fin, min: h.debut, onChoisir: (v) => poser(k, { fin: v || '' }) })}>
              <Text style={[s.horTxt, !h.fin && { color: UI.faint }]}>{h.fin || '--:--'}</Text>
            </TouchableOpacity>
            {/* Une seule plage : rien a retirer ligne par ligne — c'est le
                champ entier qui part, au swipe. */}
            {plages.length > 1 && (
              <TouchableOpacity onPress={() => retirerPlage(k)} hitSlop={{ top: 8, bottom: 8, left: 6, right: 6 }}
                accessibilityLabel={t('commun.supprimer')}>
                <Text style={s.horCroix}>✕</Text>
              </TouchableOpacity>
            )}
          </View>
        ))}
        <View style={s.horPied}>
          <TouchableOpacity activeOpacity={0.7} onPress={ajouterPlage} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Text style={{ color: UI.accent, fontWeight: '700', fontSize: 13 }}>＋ {t('contacts.ajouterHoraire')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const valeurWidget = (e, i) => {
    if (e.saisie === 'horaire') return ligneHoraire(e, i);
    if (e.saisie === 'date') {
      return (
        <TouchableOpacity style={s.dateBtn} activeOpacity={0.7} onPress={() => choisirDate(i, e.valeur)}>
          <Text style={{ fontSize: 15 }}>📅</Text>
          <Text style={[s.dateTxt, !e.valeur && { color: UI.faint }]}>{e.valeur ? frJMA(e.valeur) : t('commun.choisirDate')}</Text>
        </TouchableOpacity>
      );
    }
    return (
      <TextInput style={[s.input, { marginTop: 8 }]} value={e.valeur} onChangeText={v => setEntry(i, { valeur: v })}
        keyboardType={saisieDef(e.saisie).clavier} autoCapitalize="none"
        placeholder={t('contacts.phValeur')} placeholderTextColor={UI.faint} />
    );
  };

  return (
    <View style={{ marginTop: 4 }}>
      <Text style={s.lbl}>{t('contacts.champsPerso')}</Text>
      {entries.map((e, i) => {
        const perso = estChampPerso(e.cle);
        return (
          <LigneSwipe key={e.cle + i} espacement={8}
            onSupprimer={() => retirer(i)}
            onEditer={() => ouvrirEdition(i)}>
          {/* Appui long : SEUL recours sur le web, ou le swipe n'est pas fiable.
              Il propose les DEUX gestes, puisque le swipe en porte deux. */}
          <TouchableOpacity activeOpacity={1} style={[s.bloc, { marginTop: 0 }]}
            onLongPress={() => onDemanderChoix?.({
              titre: e.nom || t('contacts.champPerso'), emoji: e.emoji || '🏷️',
              options: [
                { id: 'editer', libelle: `✏️  ${t('contacts.modifierChamp')}` },
                { id: 'supprimer', libelle: `🗑️  ${t('commun.supprimer')}` },
              ],
              onChoisir: (id) => (id === 'editer'
                ? ouvrirEdition(i)
                : confirmerSuppression(t('commun.supprimer'), e.nom || '', () => retirer(i))),
            })}>
            <View style={s.tete}>
              {perso && (edite === i || !e.nom) ? (
                <>
                  <TextInput style={s.emoji} value={e.emoji} onChangeText={v => setEntry(i, { emoji: v })} maxLength={2} />
                  <TextInput style={[s.input, { flex: 1, marginTop: 0 }]} value={e.nom} onChangeText={v => setEntry(i, { nom: v })}
                    placeholder={t('contacts.champPersoNom')} placeholderTextColor={UI.faint} />
                </>
              ) : (
                <Text style={s.nom}>{e.emoji}  {e.nom}</Text>
              )}
            </View>
            {perso && (edite === i || !e.nom) && (
              <View style={{ marginTop: 8 }}>
                <ChoixPuces options={SAISIES_CHAMP.map(x => ({ id: x.id, label: `${x.emoji} ${x.label}` }))}
                  value={e.saisie} onChange={v => setEntry(i, { saisie: v, valeur: '', emoji: emojiSuivantType(e, v) })} />
                {edite === i && !!e.nom && (
                  <TouchableOpacity onPress={() => setEdite(null)} style={s.termine}>
                    <Text style={s.termineTxt}>{t('commun.termine')}</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
            {valeurWidget(e, i)}
          </TouchableOpacity>
          </LigneSwipe>
        );
      })}
      <TouchableOpacity style={s.ajouter} activeOpacity={0.7} onPress={demanderChamp}>
        <Text style={{ color: UI.accent, fontWeight: '700' }}>＋ {t('contacts.ajouterChamp')}</Text>
      </TouchableOpacity>
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  bloc: { marginTop: 8, padding: 10, backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border },
  tete: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  nom: { flex: 1, fontSize: 14, fontWeight: '700', color: UI.ink },
  termine: { alignSelf: 'flex-end', paddingVertical: 6, paddingHorizontal: 4, marginTop: 6 },
  termineTxt: { color: UI.accent, fontWeight: '700', fontSize: 13 },
  emoji: { width: 46, textAlign: 'center', fontSize: 18, color: UI.ink, backgroundColor: UI.bg, borderRadius: 10, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 8 },
  dateBtn: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 8, backgroundColor: UI.bg, borderRadius: 10, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 12, paddingHorizontal: 12 },
  dateTxt: { fontSize: 14.5, color: UI.ink, fontWeight: '600' },
  liste: { marginTop: 8, backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, overflow: 'hidden' },
  opt: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 11, paddingHorizontal: 12, borderBottomWidth: 0.5, borderBottomColor: UI.border },
  optTxt: { flex: 1, fontSize: 14, color: UI.ink },
  ajouter: { paddingVertical: 9, alignItems: 'center', marginTop: 4 },
  horLigne: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 8 },
  horBtn: { paddingVertical: 10, paddingHorizontal: 8, backgroundColor: UI.champ, borderRadius: 10,
            borderWidth: 0.5, borderColor: UI.champBord, alignItems: 'center' },
  horTxt: { fontSize: 14, fontWeight: '700', color: UI.ink },
  horFleche: { fontSize: 14, color: UI.muted, fontWeight: '700' },
  horPied: { alignItems: 'center', paddingVertical: 7, gap: 3 },
  horCroix: { fontSize: 15, color: UI.muted, fontWeight: '700', paddingHorizontal: 2 },
});
